import React, { ReactNode, useRef, useState } from "react";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { FileType } from "../../../types/File";

export type ImageCarouselProps = {
  imageUrls: FileType[];
  children: React.ReactNode;
  pageControl: (
    currentPage: number,
    goToPage: (offset: number, behavior?: ScrollBehavior) => void,
  ) => ReactNode;

  placeholder: React.ReactNode;
  apiUrl?: string;
  /** Optional callback when page changes */
  onPageChange?: (page: number) => void;
};

const ImageCarousel = ({
  imageUrls,
  children,
  onPageChange,
  placeholder,
  pageControl,
}: ImageCarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const slidesCount = imageUrls.length;
  const [currentPage, setCurrentPage] = useState(0);

  // Scroll container to a given page index
  const scrollToPage = (page: number, behavior: ScrollBehavior = "smooth") => {
    if (!containerRef.current) return;
    const totalWidth = containerRef.current.scrollWidth;
    const pageWidth = totalWidth / slidesCount;
    containerRef.current.scrollTo({
      left: pageWidth * page,
      behavior: behavior,
    });
  };

  // Exposed method
  const goToPage = (offset: number, behavior: ScrollBehavior = "smooth") => {
    const next = Math.max(0, Math.min(slidesCount - 1, currentPage + offset));
    if (next === currentPage) return;
    scrollToPage(next, behavior);
  };

  // Update on manual scroll
  const onScroll = () => {
    if (!containerRef.current) return;
    const totalWidth = containerRef.current.scrollWidth;
    const pageWidth = totalWidth / slidesCount;
    const idx = Math.round(containerRef.current.scrollLeft / pageWidth);
    if (idx !== currentPage) {
      setCurrentPage(idx);
      onPageChange?.(idx);
    }
  };

  return (
    <>
      {slidesCount > 0 && (
        <div className="relative w-full mx-auto">
          <div
            className="carousel w-full h-auto touch-auto overflow-x-auto whitespace-nowrap"
            ref={containerRef}
            onScroll={onScroll}
          >
            {children}
            {slidesCount === 0 && placeholder}
          </div>

          <button
            type="button"
            onClick={() => goToPage(-1)}
            className="absolute btn bg-base-300/70 hover:bg-base-300/90 btn-circle left-2 inset-y-1/2 "
            hidden={currentPage === 0}
          >
            <IconChevronLeft />
          </button>

          <button
            type="button"
            onClick={() => goToPage(1)}
            className="absolute btn bg-base-300/70 hover:bg-base-300/90 btn-circle right-2 inset-y-1/2"
            hidden={currentPage + 1 >= slidesCount}
          >
            <IconChevronRight />
          </button>
        </div>
      )}

      {pageControl(currentPage, goToPage)}
    </>
  );
};
export default ImageCarousel;
